import React, {
  useEffect,
  useCallback,
  useState,
  useContext,
  useMemo,
} from "react";
import noop from "lodash/noop";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Issue,
  IssueSortingDirection,
  IssueSortingField,
} from "@adsk/acc-issues-types";
import {
  useIsFilteredApplied,
  useIsSearched,
  useIssuesFetchParams,
} from "@adsk/acc-issues-core/state/issuesFetchParams/useIssueFetchParams";
import {
  useSelector,
  useDispatch,
} from "@adsk/acc-issues-core/state/redux/hooks";
import {
  fetchIssuesAction,
  getIssuesPagination,
  getOrderedIssues,
  getIsLoadingIssueCollection,
  getHasIssuesCollectionFetchErrored,
  Sheet,
  getErrorMessage,
  getErrorStatus,
} from "@adsk/acc-issues-core/state/redux/ducks/issuesCollection";
import { useIssuesTableData } from "./useIssuesTableData";
import {
  getIssueTableSelectedIds,
  addIdsToSelectedIdsAction,
  removeIdsFromSelectedIdsAction,
  clearSelectedIdsAction,
} from "@adsk/acc-issues-core/state/redux/ducks/issueTableSelection";
import { useIsMetadataLoading } from "@adsk/acc-issues-common/hooks/state/useIsMetadataLoading";
import { CenteredSpinner } from "@adsk/acc-issues-common/components/primitive/CenteredSpinner";
import {
  COLUMN_KEY_BY_SORT_ATTRS,
  ISSUE_COLUMN_DATA_KEYS,
  ISSUE_COLUMN_SORT_ATTRS,
  ISSUE_COLUMN_KEYS,
  SELECT_COLUMN,
  ACTIONS_COLUMN,
} from "./columnKeys";
import {
  EmptyStateForNoIssuesInProject,
  EmptyStateNoIssuesInCurrentFilter,
  EmptyStateIssuesFetchError,
  EmptyStateIssuesFetchLocationError,
  EmptyStateForNoDeletedIssuesInProject,
  EmptyStateFiltersOrSortingError,
} from "@adsk/acc-issues-common/components/display/EmptyStates";
import { ReportConfirmationModal } from "../../../utils/components/ReportConfirmationModal";
import { useTranslations } from "@adsk/acc-issues-translations";
import { Pagination } from "@adsk/acc-issues-types";
import { trackIssueLogPagination } from "../../../utils/analytics/issueNavigationTracking";
import { trackSortApply } from "../../../utils/analytics/trackFiltering";
import { DeletedIssuesSpaceContext } from "@adsk/acc-issues-core/providers/DeletedIssuesSpaceContext";
import { useExoPerformanceTracking } from "../../../utils/exoPerformanceTracking/useExoPerformanceTracking";
import {
  TRACKING_PERFORMANCE_PAGES,
  TRACKING_PERFORMANCE_COMPONENTS,
} from "../../../utils/exoPerformanceTracking/trackingPerformanceEvents";
import { IssuesTableProps } from "../IssuesTable/IssuesTable";
import { useCurrentRightPanel } from "../useCurrentRightPanel";
import {
  useReactTable,
  SortingState,
  Updater,
  RowSelectionState,
  Row,
  Table,
} from "@tanstack/react-table";
import {
  TableHeader,
  TableRow,
  useTable,
  TABLE_SIZES,
  TABLE_SIZE_THEMES,
  PaginationFooter,
  useSkeletonLoadingRows,
  TableSkeletonLoadingRow,
} from "@adsk/alloy-react-table";
import type { TableSize } from "@adsk/alloy-react-table/es/types";
import { TableConfigurationMenu } from "./TableConfigurationMenu";
import { DEFAULT_ISSUES_SORT } from "@adsk/acc-issues-core/state/issuesFetchParams/defaultParams";
import { useUserFetchParams } from "@adsk/acc-issues-core/state/issuesFetchParams/UserFetchParamsProvider";
import {
  getTableCellStyle,
  TableContainer,
  ScrollableTableWrapper,
  StyledTable,
  StickyTableHead,
  IssuesTableWrapper,
  getTableHeaderStyle,
  StyledTableHeaderGroup,
} from "./issuesTableStyles";
import { IssuesTableRow } from "./IssuesTableRow";
import { useTableConfiguration } from "./useTableConfiguration";

export const useFetchIssues = () => {
  const dispatch = useDispatch();
  const { value: issuesFetchParams, isLoading } = useIssuesFetchParams();

  const isDeletedIssuesSpaceActive = useContext(DeletedIssuesSpaceContext);

  useEffect(() => {
    if (isLoading || !issuesFetchParams) {
      return;
    }

    dispatch(fetchIssuesAction(issuesFetchParams, isDeletedIssuesSpaceActive));
  }, [dispatch, isDeletedIssuesSpaceActive, isLoading, issuesFetchParams]);
};

type IssueRow = Issue & {
  sheet?: Sheet | undefined;
};

// This hook lets us keep track of the current pagination values while also
// allowing manipulation of a local 'offset' value.
// This is useful for controlling the table's offset independently of the
// store, and we can still leverage an effect to keep the values in sync.
const useIssuesPaginationWithLocalOffset = () => {
  const issuesPagination: Pagination = useSelector(getIssuesPagination)!;
  const [paginationOffset, setPaginationOffset] = useState(0);

  useEffect(() => {
    issuesPagination && setPaginationOffset(issuesPagination.offset);
  }, [issuesPagination]);

  return { issuesPagination, paginationOffset, setPaginationOffset };
};

/**
 * Calculates top padding for virtual scroll - the space before the first visible row.
 * This maintains scroll position when only a subset of rows are rendered.
 */
const calculateVirtualScrollTopPadding = (
  virtualRows: Array<{ start?: number }>
): number => {
  return virtualRows.length > 0 ? virtualRows[0]?.start || 0 : 0;
};

/**
 * Calculates bottom padding for virtual scroll - the space after the last visible row.
 * This ensures the scrollbar reflects the true size of the entire list.
 */
const calculateVirtualScrollBottomPadding = (
  virtualRows: Array<{ end?: number }>,
  totalSize: number
): number => {
  return virtualRows.length > 0
    ? totalSize - (virtualRows[virtualRows.length - 1]?.end || 0)
    : 0;
};

export const IssueTable = ({
  initialIssueId,
  onIssueClick = noop,
  renderEmptyState,
}: IssuesTableProps) => {
  const dispatch = useDispatch();
  const { handleSortChange, handleOffsetChange } = useUserFetchParams();
  const isSearched = useIsSearched();
  const isFilteredApplied = useIsFilteredApplied();
  const isErrored = useSelector(getHasIssuesCollectionFetchErrored);
  const errorMessage = useSelector(getErrorMessage);
  const errorStatus = useSelector(getErrorStatus);
  const issues = useSelector(getOrderedIssues) as IssueRow[];
  const issuesTableData = useIssuesTableData(issues, {
    shouldFetchAssociations: false,
  });
  const selectedIssueIds = useSelector(getIssueTableSelectedIds);
  const isLoadingIssues = useSelector(getIsLoadingIssueCollection);
  const isMetadataLoading = useIsMetadataLoading();
  const { activateIssueDetailPanel, activeIssueId } = useCurrentRightPanel();

  useEffect(() => {
    if (initialIssueId) {
      activateIssueDetailPanel(initialIssueId);
    }
  }, [initialIssueId, activateIssueDetailPanel]);

  const { issuesPagination, paginationOffset, setPaginationOffset } =
    useIssuesPaginationWithLocalOffset();

  const isDeletedSpaceActive = useContext(DeletedIssuesSpaceContext);

  useEffect(() => {
    dispatch(clearSelectedIdsAction());
  }, [dispatch]);

  useFetchIssues();
  useExoPerformanceTracking({
    displayName: `${TRACKING_PERFORMANCE_PAGES.ISSUES_LOG} - ${TRACKING_PERFORMANCE_COMPONENTS.TABLE}`,
    loading: isLoadingIssues,
  });

  const onRowClick = useCallback(
    ({ rowKey: issueId, rowData: Issue }) => {
      activateIssueDetailPanel(issueId);
      onIssueClick(Issue);
    },
    [onIssueClick, activateIssueDetailPanel]
  );

  const checkedRowIds = useMemo(
    () =>
      selectedIssueIds.reduce(
        (acc: Record<string, boolean>, id: string) => ({ ...acc, [id]: true }),
        {}
      ),
    [selectedIssueIds]
  );

  const handleRowCheckboxChange = useCallback(
    (toSelect: string[], toDeselect: string[]) => {
      if (toSelect.length > 0) {
        dispatch(addIdsToSelectedIdsAction({ issueUids: toSelect }));
      }
      if (toDeselect.length > 0) {
        dispatch(removeIdsFromSelectedIdsAction({ issueUids: toDeselect }));
      }
    },
    [dispatch]
  );

  const defaultEmptyState = useMemo(() => {
    if (isSearched || isFilteredApplied) {
      return <EmptyStateNoIssuesInCurrentFilter />;
    } else {
      if (isDeletedSpaceActive) {
        return <EmptyStateForNoDeletedIssuesInProject />;
      } else {
        return <EmptyStateForNoIssuesInProject />;
      }
    }
  }, [isDeletedSpaceActive, isFilteredApplied, isSearched]);

  if (isMetadataLoading) {
    return <CenteredSpinner />;
  }
  if (isErrored) {
    const locationSizeRegex =
      /(exceeded max number of sub nodes - (?<locationLimit>\d*))/i;
    const locationErrorMatch = errorMessage?.match?.(locationSizeRegex);

    if (locationErrorMatch) {
      const {
        groups: { locationLimit },
      } = locationErrorMatch;

      return (
        <EmptyStateIssuesFetchLocationError locationLimit={locationLimit} />
      );
    }

    if (errorStatus === 400) {
      return <EmptyStateFiltersOrSortingError />;
    }

    return <EmptyStateIssuesFetchError />;
  }

  const isEmptyState = !isLoadingIssues && !issuesTableData.length;
  if (isEmptyState) {
    return renderEmptyState
      ? renderEmptyState(defaultEmptyState, {
          isSearched,
          isFiltered: isFilteredApplied,
        })
      : defaultEmptyState;
  }

  return (
    <IssuesTableWrapper data-testid="issues-table">
      <IssueTableView
        data={isLoadingIssues ? [] : issuesTableData}
        onRowClick={onRowClick}
        selectedIssueId={activeIssueId}
        onRowCheckboxChange={handleRowCheckboxChange}
        checkedRowIds={checkedRowIds}
        issuesPagination={issuesPagination}
        paginationOffset={paginationOffset}
        setPaginationOffset={setPaginationOffset}
        handleOffsetChange={handleOffsetChange}
        handleSortChange={handleSortChange}
        isLoading={isLoadingIssues}
      />
    </IssuesTableWrapper>
  );
};

type IssueTableData = ReturnType<typeof useIssuesTableData>;

const IssueTableComponent = ({
  data,
  onRowClick,
  selectedIssueId,
  onRowCheckboxChange,
  checkedRowIds,
  issuesPagination,
  paginationOffset,
  setPaginationOffset,
  handleOffsetChange,
  handleSortChange,
  isLoading,
}: {
  data: IssueTableData;
  onRowClick: (params: { rowKey: string; rowData: Issue }) => void;
  selectedIssueId: string | undefined;
  onRowCheckboxChange: (toSelect: string[], toDeselect: string[]) => void;
  checkedRowIds: Record<string, boolean>;
  issuesPagination: Pagination | undefined;
  paginationOffset: number;
  setPaginationOffset: (offset: number) => void;
  handleOffsetChange: (offset: number) => void;
  handleSortChange: (sortParams: {
    attribute: IssueSortingField;
    direction: IssueSortingDirection;
  }) => void;
  isLoading: boolean;
}) => {
  const [issueForDetailReport, setIssueForDetailReport] = useState<Issue>();
  const [isReportConfirmationModalOpen, setIsReportConfirmationModalOpen] =
    useState(false);
  const { T } = useTranslations();

  const handleExport = useCallback((issue: Issue) => {
    setIssueForDetailReport(issue);
    setIsReportConfirmationModalOpen(true);
  }, []);

  const extractCustomFieldId = (attribute: string): string => {
    const match = attribute.match(/^customAttributes\[(.+)\]$/);
    return match?.[1] || "";
  };
  const { value: issuesFetchParams } = useIssuesFetchParams();

  const currentSort = issuesFetchParams?.sortBy;

  const externalSorting: SortingState = useMemo(() => {
    if (!currentSort) return [];

    const isCustomField =
      currentSort.attribute.startsWith("customAttributes[") &&
      currentSort.attribute.endsWith("]");

    if (isCustomField) {
      const customFieldId = extractCustomFieldId(currentSort.attribute);
      return [
        {
          id: `custom_${customFieldId}`,
          desc: currentSort.direction === IssueSortingDirection.Desc,
        },
      ];
    }

    const columnKey = COLUMN_KEY_BY_SORT_ATTRS[currentSort.attribute];
    if (!columnKey) return [];

    return [
      {
        id: columnKey,
        desc: currentSort.direction === IssueSortingDirection.Desc,
      },
    ];
  }, [currentSort]);

  const calculateSortParams = useCallback((newSorting: SortingState): {
    attribute: IssueSortingField;
    direction: IssueSortingDirection;
  } => {
    const sort = newSorting[0];
    const direction = sort.desc
      ? IssueSortingDirection.Desc
      : IssueSortingDirection.Asc;

    if (sort.id.startsWith("custom_")) {
      const customFieldId = sort.id.replace("custom_", "");
      return {
        attribute: `customAttributes[${customFieldId}]` as IssueSortingField,
        direction,
      };
    }

    // Handle standard issue column sorting
    const columnKey = sort.id as ISSUE_COLUMN_KEYS;
    const sortingField = ISSUE_COLUMN_SORT_ATTRS[columnKey];

    return {
      // Fallback to default sorting attribute (ID) if sortingField is undefined.
      // This happens when a non-sortable column somehow triggers a sort event, providing a safety net to prevent crashes.
      attribute: sortingField || DEFAULT_ISSUES_SORT.attribute,
      direction,
    };
  }, []);

  const handleSortingChange = useCallback(
    updaterOrValue => {
      const newSorting =
        typeof updaterOrValue === "function"
          ? updaterOrValue(externalSorting)
          : updaterOrValue;

      const sortParams = calculateSortParams(newSorting);
      const sortedColumnId = newSorting[0].id;
      const analyticsDataKey =
        ISSUE_COLUMN_DATA_KEYS[sortedColumnId as ISSUE_COLUMN_KEYS] ??
        sortParams.attribute;
      trackSortApply(analyticsDataKey, sortParams.direction);
      handleSortChange(sortParams);
    },
    [externalSorting, handleSortChange, calculateSortParams]
  );

  const checkedRowIdsRef = React.useRef(checkedRowIds);
  React.useEffect(() => {
    checkedRowIdsRef.current = checkedRowIds;
  }, [checkedRowIds]);

  const handleRowCheckboxChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      const currentChecked = checkedRowIdsRef.current;
      const newSelection =
        typeof updater === "function" ? updater(currentChecked) : updater;

      const toSelect = Object.keys(newSelection).filter(
        id => !currentChecked[id]
      );
      const toDeselect = Object.keys(currentChecked).filter(
        id => !newSelection[id]
      );

      onRowCheckboxChange(toSelect, toDeselect);
    },
    [onRowCheckboxChange]
  );

  const {
    tableConfig,
    columnVisibility,
    columnOrder,
    columnSizing,
    tableSize,
    containerRef,
    setTableSize,
  } = useTableConfiguration({
    data,
    isLoading,
    externalSorting,
    checkedRowIds,
    handleSortingChange,
    handleRowCheckboxChange,
    handleExport,
  });

  const table = useReactTable(tableConfig);

  const useTableConfig = useMemo(
    () => ({ table, tableSize }),
    [table, tableSize]
  );

  const {
    getTableHeaderGroupProps,
    getTableHeaderProps,
    getTableRowProps,
    getTableCellProps,
  } = useTable(useTableConfig);

  const isResizingColumn = table.getState().columnSizingInfo.isResizingColumn;

  // Pagination handlers and calculations
  const limit = issuesPagination?.limit || 50;
  const totalResults = issuesPagination?.totalResults || 0;
  const pageIndex = Math.floor(paginationOffset / limit);
  const pageCount = Math.ceil(totalResults / limit);
  const canPreviousPage = pageIndex > 0;
  const canNextPage = pageIndex < pageCount - 1;

  const handlePageIndexChange = useCallback(
    (newPageIndex: number) => {
      const newOffset = newPageIndex * limit;
      handleOffsetChange(newOffset);
      setPaginationOffset(newOffset);
      trackIssueLogPagination(newPageIndex > pageIndex ? "next" : "previous");
    },
    [handleOffsetChange, setPaginationOffset, limit, pageIndex]
  );

  const checkedIssueCount = useMemo(
    () => Object.keys(checkedRowIds).length,
    [checkedRowIds]
  );
  const pageStartIndex = paginationOffset + 1;
  const pageEndIndex = Math.min(paginationOffset + limit, totalResults);

  const paginationText: string =
    checkedIssueCount > 0
      ? (T.translate("issueTable.itemsSelectedInfo", {
          selectedNumber: checkedIssueCount,
          total: totalResults,
        }) as string) || ""
      : (T.translate("issueTable.paginationInfo", {
          pageStartIndex,
          pageEndIndex,
          total: totalResults,
        }) as string) || "";

  const rows = table.getRowModel().rows;

  const rowHeight = useMemo(() => {
    const theme =
      TABLE_SIZE_THEMES[tableSize] || TABLE_SIZE_THEMES[TABLE_SIZES.MEDIUM];
    return theme.rowHeight || 40;
  }, [tableSize]);

  const estimateRowSize = useCallback(() => rowHeight, [rowHeight]);

  const getScrollElement = useCallback(
    () => containerRef.current,
    [containerRef]
  );

  const rowVirtualizer = useVirtualizer({
    count: isLoading ? issuesPagination?.limit || 50 : rows.length,
    getScrollElement,
    estimateSize: estimateRowSize,
    overscan: 5,
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = calculateVirtualScrollTopPadding(virtualRows);
  const paddingBottom = calculateVirtualScrollBottomPadding(
    virtualRows,
    totalSize
  );

  const virtualRowsData = useMemo(() => {
    return virtualRows.map(virtualRow => {
      const row = rows[virtualRow.index];
      if (!row) return null;

      return {
        row,
        id: row.id,
      };
    });
  }, [virtualRows, rows]);

  return (
    <TableContainer>
      <ScrollableTableWrapper ref={containerRef}>
        <StyledTable>
          <IssuesTableHeader
            table={table}
            getTableHeaderGroupProps={getTableHeaderGroupProps}
            getTableHeaderProps={getTableHeaderProps}
            tableSize={tableSize}
            setTableSize={setTableSize}
          />
          <IssuesTableBody
            isLoading={isLoading}
            table={table}
            issuesPagination={issuesPagination}
            onRowClick={onRowClick}
            getTableRowProps={getTableRowProps}
            getTableCellProps={getTableCellProps}
            tableSize={tableSize}
            checkedRowIds={checkedRowIds}
            selectedIssueId={selectedIssueId}
            virtualRowsData={virtualRowsData}
            columnSizing={columnSizing}
            columnVisibility={columnVisibility}
            columnOrder={columnOrder}
            resizingColumn={isResizingColumn}
            paddingTop={paddingTop}
            paddingBottom={paddingBottom}
          />
        </StyledTable>
      </ScrollableTableWrapper>

      {issuesPagination && (
        <PaginationFooter
          canPreviousPage={canPreviousPage}
          canNextPage={canNextPage}
          pageCount={pageCount}
          setPageIndex={handlePageIndexChange}
          pageIndex={pageIndex}
          pageSize={limit}
          totalDataCount={totalResults}
          text={paginationText}
        />
      )}
      <ReportConfirmationModal
        isOpen={isReportConfirmationModalOpen}
        closeReportModal={() => setIsReportConfirmationModalOpen(false)}
        issue={issueForDetailReport}
      />
    </TableContainer>
  );
};

const IssueTableView = React.memo(IssueTableComponent);

const SkeletonLoadingRows = ({
  table,
  numberOfRows,
}: {
  table: any;
  numberOfRows: number;
}) => {
  const { getSkeletonLoadingRows, getSkeletonLoadingRowProps } =
    useSkeletonLoadingRows({
      table: table as any,
      numberOfRows,
    });

  return (
    <>
      {getSkeletonLoadingRows().map(skeletonRow => (
        <TableSkeletonLoadingRow
          key={skeletonRow.id}
          {...getSkeletonLoadingRowProps(skeletonRow)}
        />
      ))}
    </>
  );
};

type IssuesTableInstance = Table<Issue>;

interface IssuesTableHeaderProps {
  table: IssuesTableInstance;
  getTableHeaderGroupProps: any;
  getTableHeaderProps: any;
  tableSize: TableSize;
  setTableSize: (size: TableSize) => void;
}

const IssuesTableHeader = ({
  table,
  getTableHeaderGroupProps,
  getTableHeaderProps,
  tableSize,
  setTableSize,
}: IssuesTableHeaderProps) => {
  const headerGroups = table.getHeaderGroups();

  return (
    <StickyTableHead data-testid="table-head">
      {headerGroups.map(headerGroup => (
        <StyledTableHeaderGroup
          key={headerGroup.id}
          {...getTableHeaderGroupProps(headerGroup)}
        >
          {headerGroup.headers.map(header => {
            const { column } = header;
            const isActionsColumn = column.id === ACTIONS_COLUMN;
            const isSelectColumn = column.id === SELECT_COLUMN;
            const headerProps = getTableHeaderProps(header) as any;

            return (
              <TableHeader
                key={header.id}
                {...headerProps}
                data-testid={`table-header-${header.id}`}
                data-column-id={header.id}
                style={getTableHeaderStyle(
                  isActionsColumn,
                  isSelectColumn,
                  headerProps.style || {},
                  column
                )}
              >
                {isActionsColumn && (
                  <TableConfigurationMenu
                    table={table}
                    tableSize={tableSize}
                    setTableSize={setTableSize}
                  />
                )}
              </TableHeader>
            );
          })}
        </StyledTableHeaderGroup>
      ))}
    </StickyTableHead>
  );
};

interface VirtualRowData {
  row: Row<Issue>;
  id: string;
}

interface IssuesTableBodyProps {
  isLoading: boolean;
  table: IssuesTableInstance;
  issuesPagination: Pagination | undefined;
  onRowClick: (params: { rowKey: string; rowData: Issue }) => void;
  getTableRowProps: any;
  getTableCellProps: any;
  tableSize: TableSize;
  checkedRowIds: Record<string, boolean>;
  selectedIssueId: string | undefined;
  virtualRowsData: (VirtualRowData | null)[];
  columnSizing: Record<string, number>;
  columnVisibility: Record<string, boolean>;
  columnOrder: string[];
  // Type comes from `@tanstack/react-table`, `columnSizingInfo.isResizingColumn`.
  resizingColumn: false | string;
  paddingTop: number;
  paddingBottom: number;
}

const IssuesTableBodyComponent = ({
  isLoading,
  table,
  issuesPagination,
  onRowClick,
  getTableRowProps,
  getTableCellProps,
  tableSize,
  checkedRowIds,
  selectedIssueId,
  virtualRowsData,
  columnSizing,
  columnVisibility,
  columnOrder,
  resizingColumn,
  paddingTop,
  paddingBottom,
}: IssuesTableBodyProps) => {
  const tableCellStyle = useMemo(
    () => getTableCellStyle(tableSize),
    [tableSize]
  );

  const paddingTopStyle = useMemo(
    () => ({ height: `${paddingTop}px` }),
    [paddingTop]
  );
  const paddingBottomStyle = useMemo(
    () => ({ height: `${paddingBottom}px` }),
    [paddingBottom]
  );

  return (
    <tbody>
      {isLoading ? (
        <SkeletonLoadingRows
          table={table}
          numberOfRows={issuesPagination?.limit || 50}
        />
      ) : (
        <>
          {paddingTop > 0 && <TableRow style={paddingTopStyle} />}
          {virtualRowsData.map(rowData => {
            if (!rowData) return null;
            const { row, id } = rowData;
            const isChecked = !!checkedRowIds[id];
            const isSelected = selectedIssueId === id;

            return (
              <IssuesTableRow
                key={id}
                row={row}
                onRowClick={onRowClick}
                getTableRowProps={getTableRowProps}
                getTableCellProps={getTableCellProps}
                tableCellStyle={tableCellStyle}
                columnSizing={columnSizing}
                columnVisibility={columnVisibility}
                columnOrder={columnOrder}
                resizingColumn={resizingColumn}
                isSelected={isSelected}
                isChecked={isChecked}
              />
            );
          })}

          {paddingBottom > 0 && <TableRow style={paddingBottomStyle} />}
        </>
      )}
    </tbody>
  );
};

const IssuesTableBody = React.memo(IssuesTableBodyComponent);
